import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import { Button } from 'antd';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';

// icons
import MS_ICON from '../data/icons8-microsoft-48.png';
// FB
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  SAMLAuthProvider,
  signOut
} from 'firebase/auth';

import { auth, googleAuthProvider, msAuthProvider } from '../config/fb';
// redux
import { setReset, setCurrentPage, setPrivacy, setAppVerbosity, setAppReactVerbosity, setSubscriptionIssueType} from '../redux/app.slice';
/* eslint-disable */
import {
  setUser,
  setName,
  setOrganization,
  setRole,
  setLoginStatus,
  setIdToken,
  resetUser,
  setEmail,
  setLimitedAccess
} from "../redux/user.slice"; 
import { setToken, setIsRefreshing, clearToken } from '../redux/token.slice';

/* eslint-enable */
// APIs
import { deleteSession, setCurrentUser, reloadSession } from '../apis/user.api';
import { fetchAppVerbosityApi, updateApplicationApi } from '../apis/app.api';
import { writeLoginErrorToS3Api, writeLoginSuccessToS3Api, sendErrorByEmail } from '../apis/logs.api';
import { checkSignInMethodApi, updateSignInMethodApi } from '../apis/admin.api';
import { subscriptionApi } from '../apis/subscription.api';
// UTILS
import capitalizeFirstLetter from '../utils/capitalizeFirstLetter';


// Accounts made by the trial system (server/services/trial). Once the trial ends
// the server refuses them, and the expiry sweep disables their Firebase login.
const TRIAL_EMAIL_DOMAIN = '@lavenirai-trial.com';
const isTrialEmail = (e) => typeof e === 'string' && e.trim().toLowerCase().endsWith(TRIAL_EMAIL_DOMAIN);
const TRIAL_ENDED_MESSAGE =
  'This trial has ended, so this account can no longer sign in. To continue, contact the LavenirAI team member who set up your trial, or support@lavenirai.com.';

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [ email, setFormEmail ] = useState('');
  const [ password, setPassword ] = useState('');
  const [ loading, setLoading ] = useState(false);
  const [ errorMessage, setErrorMessage ] = useState(null);
  const { reset } = useSelector((state) => state.app, shallowEqual);
  const { loginStatus } = useSelector((state) => state.user, shallowEqual); // eslint-disable-line
  
  const [showPassword, setShowPassword] = useState(false);
  const passwordRef = useRef(null);

  // One /check-signin-method response per email address, shared by every sign in
  // path on this page (email, Google, EntraID) so we only ever make the call once.
  const signInMethodCache = useRef({});

  // Add form readiness states similar to Register
  const [isReady, setIsReady] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const formRef = useRef(null);

  // Add state for confirmation dialog
  const [showGoogleConfirmation, setShowGoogleConfirmation] = useState(false);
  const [showEntraIDConfirmation, setShowEntraIDConfirmation] = useState(false);
  
  const [pendingMessage, setPendingMessage] = useState('');

  const consoleHistory = useRef([]);

  // Consolidate useEffect hooks
  useEffect(() => {
    dispatch(setCurrentPage('login'));
    const timer = setTimeout(() => {
      setIsReady(true);
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    setIsTyping(true);
    const timer = setTimeout(() => {
      setIsTyping(false);
    }, 100);
    return () => clearTimeout(timer);
  }, [email, password]);

  // Add effect to handle redirect result
  useEffect(() => {
    const handleRedirectResult = async () => {
      console.log('Checking redirect result...');
      try {
        const result = await getRedirectResult(auth);
        console.log('Redirect result:', result);
        if (result) {
          const user = result.user;
          const idTokenResult = await user.getIdTokenResult();
          user.idToken = idTokenResult.token;

          const dbUser = await setCurrentUser(
            idTokenResult.token,
            JSON.stringify(user),
            user.email,
            result
          );

          if (Object.keys(dbUser.data).includes("error")) {
            const message = "User does not exist in this region. Try logging into your assigned region or contact support@lavenirai.com. Regions are app.lavenirai.com, app-eu.lavenirai.com, app-sg.lavenirai.com errorCode=L5";
            handleError(message, dbUser.data);
            return null;
          } else {
            await loadUserApp(idTokenResult);
            await loadSession(idTokenResult, user);
            navigate('/');
          }
        }
      } catch (err) {
        console.log('Redirect sign-in error:', err);
        const message = "Wrong password or user not registered. Please use Forgot Password link above, register, or contact support@lavenirai.com errorCode=gL1";
        handleError(message, err);
      }
    };

    handleRedirectResult();
  }, []);

  // Single source of truth for "how did this account register?".
  // The server only ever looks the email up; isMatchingMethod is just
  // registerMethod === signInMethod, so we cache the lookup per email and
  // recompute that flag locally for whichever method is being attempted.
  const resolveSignInMethod = async (emailAddress, intendedMethod) => {
    const key = (emailAddress || '').toLowerCase();
    let result = signInMethodCache.current[key];
    if (!result) {
      result = await checkSignInMethodApi(emailAddress, intendedMethod);
      signInMethodCache.current[key] = result;
    }
    return { ...result, isMatchingMethod: result.originalRegisterMethod === intendedMethod };
  };

  // Consolidate authentication handlers into a single function
  const handleAuthentication = async (authFunction, provider = null, popupConfig = null) => {
    setErrorMessage(null);
    setLoading(true);
    setShowGoogleConfirmation(false);
    setErrorMessage(null);

    // temporarily override console.log and console.error to store the messages in a buffer
    const originalLog = console.log;
    const originalError = console.error;
    console.log = (...args) => {
      consoleHistory.current.push(args);
      originalLog(...args);
    };
    console.error = (...args) => {
      consoleHistory.current.push(args);
      originalError(...args);
    };
    try {
      // Determine the current sign-in method
      const currentSignInMethod = provider 
        ? (provider === msAuthProvider ? 'microsoft' : 'google') 
        : 'email';

      if (email !== '' && isValidEmailAddress()) {
        console.log('---- handleAuthentication email, currentSignInMethod:', email, currentSignInMethod)
      // Check if sign-in method matches registration method
        try {
        const result = await resolveSignInMethod(email, currentSignInMethod);
        console.log('---- checkSignInMethodApi result:', result, result.originalRegisterMethod, currentSignInMethod);
        if (!result.isMatchingMethod && result.originalRegisterMethod != null) {
            setLoading(false);
            // const message = `Please use ${result.originalRegisterMethod} to sign in. This account was registered using ${result.originalRegisterMethod}.`;
            
            let message = `This account was registered using ${capitalizeFirstLetter(result.originalRegisterMethod)}.  If you experience issues signing in with ${currentSignInMethod}, please use ${result.originalRegisterMethod} to sign in.`
            
            console.log('!!! ---- result.originalRegisterMethod:', result.originalRegisterMethod, currentSignInMethod)
            if (result.originalRegisterMethod === 'email' && currentSignInMethod === 'microsoft') {
              message = `This account was registered using Email.  If you sign in with EntraID now, you must continue to use EntraID as your sign in method.  Email and password sign in will no longer work for this account.`
              setErrorMessage(message);
              setShowEntraIDConfirmation(true);
              return
            }

            if (result.originalRegisterMethod === 'microsoft' && currentSignInMethod === 'email') {
              message = `This account was registered using EntraID.  Please use EntraID to sign in.`
              setErrorMessage(message);
              return
            }

            if (result.originalRegisterMethod === 'email' && currentSignInMethod === 'google') {
              message = `This account was registered using ${capitalizeFirstLetter(result.originalRegisterMethod)}.  If you sign in with Google now, you must continue to use Google as your sign in method.  Email and password sign in will no longer work for this account.`
              setErrorMessage(message);
            } else if (result.originalRegisterMethod === 'google' && currentSignInMethod === 'email') {
              message = `This account was registered using ${capitalizeFirstLetter(result.originalRegisterMethod)}, you must now use this as your sign in method.`
              setErrorMessage(message);
              return 
            } else if (result.originalRegisterMethod === 'google' && currentSignInMethod === 'microsoft') {
              message = `This account was registered using ${capitalizeFirstLetter(result.originalRegisterMethod)}, you must now use this as your sign in method.` 
              setErrorMessage(message);
              return
            } else if (result.originalRegisterMethod === 'microsoft' && currentSignInMethod !== 'microsoft') {
              message = `This account was registered using ${capitalizeFirstLetter(result.originalRegisterMethod)}, you must use EntraID as your sign in method.` 
              setErrorMessage(message);
              return
            } else if (result.originalRegisterMethod === 'email' && currentSignInMethod !== 'microsoft') {
              message = `This account was registered using ${capitalizeFirstLetter(result.originalRegisterMethod)}, you must use Email as your sign in method.` 
              setErrorMessage(message);
              return
            }
            
          
            handleError(message, result);
          // return null;
          }
        } catch (err) {
          console.log('Sign-in method check failed:', err);
          // Continue with authentication if the check fails
          // This prevents blocking login if the new endpoint is not yet deployed
        }
      } else {
        console.log('---- googleLogin email is empty')
        setErrorMessage(null);
      }




      const userCredential = await authFunction(auth, ...(provider ? [provider] : [email, password]));
      const user = userCredential.user;
      const idTokenResult = await user.getIdTokenResult();
      user.idToken = idTokenResult.token;

      const dbUser = await setCurrentUser(
        idTokenResult.token,
        JSON.stringify(user),
        user.email,
        provider ? userCredential : undefined
      );

      if (dbUser.data && dbUser.data.code === 'TRIAL_EXPIRED') {
        await signOut(auth);
        handleError(TRIAL_ENDED_MESSAGE, dbUser.data);
        return null;
      } else if (Object.keys(dbUser.data).includes("error")) {
        const message = "User does not exist in this region. Try logging into your assigned region or contact support@lavenirai.com. Regions are app.lavenirai.com, app-eu.lavenirai.com, app-sg.lavenirai.com errorCode=L5";
        handleError(message, dbUser.data);
        return null;
      } else {
        const appLoaded = await loadUserApp(idTokenResult);
        if (appLoaded) {
          await loadSession(idTokenResult, user);
          navigate('/');
        }
      }
    } catch (err) {
      const errCode = err?.code || '';
      if (errCode === "auth/wrong-password") {
        const message = "Wrong password. Please try again, reset password, or contact support@lavenirai.com errorCode=L1";
        handleError(message, err);
      } else if (errCode === "auth/user-disabled" && isTrialEmail(email)) {
        // The trial expiry sweep disables the Firebase account.
        handleError(TRIAL_ENDED_MESSAGE, err);
      } else {
        const message = `Failed to login, please try again or contact support@lavenirai.com errorCode=${provider ? (provider === msAuthProvider ? 'mL1' : 'gL1') : 'L1'}`;
        handleError(message, err);
      }
    } finally {
      setLoading(false);
      console.log = originalLog;
      console.error = originalError;
      consoleHistory.current = [];
    }
  };

  // Simplified error handling
  const handleError = async (message, error) => {
    setErrorMessage(message);
    const frontendLogs = consoleHistory.current;
    try {
      await writeLoginErrorToS3Api({
        email,
        error: { message, resErr: error, frontendLogs }
      });
    } catch (err) {
      console.log("writeLoginErrorToS3Api failure:", err);
    }

    // Do not send email in following cases
    if (!email) return;
    // A trial ending is expected, not a fault to be emailed to support.
    if (message === TRIAL_ENDED_MESSAGE) return;
    if (error?.isMatchingMethod === false) return;
    const err_string = JSON.stringify(error) || '';
    if (
      // err_string.includes('auth/wrong-password') ||
      (err_string.includes('auth/user-not-found') && email.includes('@gmail.com')) || // to skip spam login attempts
      err_string.includes('auth/popup-closed-by-user') ||
      err_string.includes('auth/cancelled-popup-request') ||
      err_string.includes('auth/popup-blocked')
    ) return;
    try {
      const errorLog = { message, resErr: error, frontendLogs };
      const emailData = {
        subject: "Login Error " + email,
        html: `
          <p><strong>User:</strong> ${email}</p>
          <p><strong>Message:</strong> ${message}</p>
          <p><strong>Error:</strong></p>
          <pre>${JSON.stringify(errorLog, null, 2)}</pre>
          `
      };
      await sendErrorByEmail(emailData);
    } catch (err) {
      console.log("sendErrorByEmail failure:", err);
    }
  };

  // Add custom popup configuration
  const getPopupConfig = () => {
    const width = 500;
    const height = 600;
    const left = 100;  //(window.innerWidth - width) / 8;
    const top = Math.max(0, (window.innerHeight - height) / 4); 

    return {
      popup: {
        width,
        height,
        left,
        top
      }
    };
  };

  // Simplified authentication methods
  const msEntraIDLogin = async () => {
    console.log('Starting EntraID login...');
    // Close any open confirmation dialogs
    setShowGoogleConfirmation(false); // Close Google confirmation as well
    setShowEntraIDConfirmation(false); // Close EntraID confirmation
    // temporarily override console.log and console.error to store the messages in a buffer
    const originalLog = console.log;
    const originalError = console.error;
    console.log = (...args) => {
      consoleHistory.current.push(args);
      originalLog(...args);
    };
    console.error = (...args) => {
      consoleHistory.current.push(args);
      originalError(...args);
    };
    try {
      const currentSignInMethod = 'microsoft';
      if (email !== '' && isValidEmailAddress()) {
        console.log('Checking sign-in method...');
        const result = await resolveSignInMethod(email, currentSignInMethod);
        console.log('Sign-in method check result:', result);
      
        // Handle Google registration case first - no confirmation needed
        if (result.originalRegisterMethod === 'google') {
          const message = `This account was registered using ${capitalizeFirstLetter(result.originalRegisterMethod)}, you must use Google as your sign in method.`;
          handleError(message, result);
          return;
        }

        // Handle other non-matching methods with confirmation
        if (!result.isMatchingMethod && result.originalRegisterMethod != null) {
          const message = `This account was registered using ${capitalizeFirstLetter(result.originalRegisterMethod)}. If you sign in with EntraID now, you must continue to use EntraID as your sign in method. Email and password sign in will no longer work for this account.`;
          setPendingMessage(message);
          setShowEntraIDConfirmation(true);
          handleError(message, result);
          return;
        }

        msAuthProvider.setCustomParameters({login_hint: email});
      }
      // If methods match or no original method, proceed directly
      proceedWithEntraIDSignIn();
    } catch (err) {
      console.error('EntraID login error:', err);
      const message = "Failed to start EntraID sign in, please try again or contact support@lavenirai.com errorCode=mL2";
      handleError(message, err);
    } finally {
      console.log = originalLog;
      console.error = originalError;
      consoleHistory.current = [];
    }
  };

  const proceedWithEntraIDSignIn = async () => {
    setErrorMessage(null);
    console.log('Initiating EntraID popup...');
    try {
      const userCredential = await signInWithPopup(auth, msAuthProvider);
      const user = userCredential.user;
      const idTokenResult = await user.getIdTokenResult();
      user.idToken = idTokenResult.token;

      const dbUser = await setCurrentUser(
        idTokenResult.token,
        JSON.stringify(user),
        user.email,
        userCredential
      );

      if (Object.keys(dbUser.data).includes("error")) {
        const message = "User does not exist in this region. Try logging into your assigned region or contact support@lavenirai.com. Regions are app.lavenirai.com, app-eu.lavenirai.com, app-sg.lavenirai.com errorCode=L5";
        handleError(message, dbUser.data);
        return null;
      } else {
        // Update sign-in method after successful authentication
        try {
          await updateSignInMethodApi(user.email, 'microsoft');
        } catch (err) {
          console.error('Failed to update sign-in method after authentication:', err);
          // Log but don't block the login process
        }

        await loadUserApp(idTokenResult);
        await loadSession(idTokenResult, user);
        navigate('/');
      }
    } catch (err) {
      console.error('EntraID popup error:', err);
      const errCode = err?.code || '';
      let err_json = '';
      try { err_json = JSON.stringify(err); } catch (_) {}
      if (errCode === 'auth/popup-closed-by-user' || err_json.includes('auth/popup-closed-by-user')) {
        console.log('---- EntraID login popup-closed-by-user')
      } else if (errCode === 'auth/popup-blocked' || err_json.includes('auth/popup-blocked')) {
        setErrorMessage("Popup window blocked by the browser.");
      } else {
        handleError("Failed to start EntraID sign in, please try again or contact support@lavenirai.com errorCode=mL2", err);
      }
    }
  };

  const googleLogin = async () => {
    console.log('Starting Google login...');
    // Close any open confirmation dialogs
    setShowGoogleConfirmation(false);
    setShowEntraIDConfirmation(false); // Close EntraID confirmation as well
    // temporarily override console.log and console.error to store the messages in a buffer
    const originalLog = console.log;
    const originalError = console.error;
    console.log = (...args) => {
      consoleHistory.current.push(args);
      originalLog(...args);
    };
    console.error = (...args) => {
      consoleHistory.current.push(args);
      originalError(...args);
    };
    try {
      const currentSignInMethod = 'google';
      console.log('---- googleLogin email:', email);
      if (email !== '' && isValidEmailAddress()) {
        console.log('Checking sign-in method...');
        const result = await resolveSignInMethod(email, currentSignInMethod);
        console.log('Sign-in method check result:', result);

        // Handle Microsoft registration case first - no confirmation needed
        if (result.originalRegisterMethod === 'microsoft') {
          const message = `This account was registered using ${capitalizeFirstLetter(result.originalRegisterMethod)}, you must use EntraID as your sign in method.`;
          handleError(message, result);
          return;
        }

        // Handle other non-matching methods with confirmation
        if (!result.isMatchingMethod && result.originalRegisterMethod != null) {
          const message = `This account was registered using ${capitalizeFirstLetter(result.originalRegisterMethod)}.  If you sign in with Google now, you must continue to use Google as your sign in method.  Email and password sign in will no longer work for this account.`;
          setPendingMessage(message);
          setShowGoogleConfirmation(true);
          handleError(message, result);
          return;
        }

        googleAuthProvider.setCustomParameters({login_hint: email});

      } else {
        setErrorMessage(null);
      }
      // If methods match or no original method, proceed directly
      proceedWithGoogleSignIn();
    } catch (err) {
      console.error('Google login error:', err);
      const message = "Failed to start Google sign in, please try again or contact support@lavenirai.com errorCode=gL2";
      handleError(message, err);
    } finally {
      console.log = originalLog;
      console.error = originalError;
      consoleHistory.current = [];
    }
  };

  // Add handler for proceeding with sign in
  const proceedWithGoogleSignIn = async () => {
    setErrorMessage(null);
    console.log('Initiating Google popup...');
    try {
      // First update the sign-in method
      // try {
      //   await updateSignInMethodApi(email, 'google');
      // } catch (err) {
      //   console.error('Failed to update sign-in method:', err);
      //   // Continue with sign-in even if update fails
      //   // We can retry the update after successful authentication
      // }

      const userCredential = await signInWithPopup(auth, googleAuthProvider);
      const user = userCredential.user;
      const idTokenResult = await user.getIdTokenResult();
      user.idToken = idTokenResult.token;

      const dbUser = await setCurrentUser(
        idTokenResult.token,
        JSON.stringify(user),
        user.email,
        userCredential
      );

      if (Object.keys(dbUser.data).includes("error")) {
        const message = "User does not exist in this region. Try logging into your assigned region or contact support@lavenirai.com. Regions are app.lavenirai.com, app-eu.lavenirai.com, app-sg.lavenirai.com errorCode=L5";
        handleError(message, dbUser.data);
        return null;
      } else {
        // Retry updating sign-in method if the first attempt failed
        try {
          await updateSignInMethodApi(user.email, 'google');
        } catch (err) {
          console.error('Failed to update sign-in method after authentication:', err);
          // Log but don't block the login process
        }

        await loadUserApp(idTokenResult);
        await loadSession(idTokenResult, user);
        navigate('/');
      }
    } catch (err) {
      console.error('Google popup error:', err);
      const errCode = err?.code || '';
      let err_json = '';
      try { err_json = JSON.stringify(err); } catch (_) {}
      if (errCode === 'auth/popup-closed-by-user' || err_json.includes('auth/popup-closed-by-user')) {
        console.log('---- googleLogin popup-closed-by-user')
      } else if (errCode === 'auth/cancelled-popup-request' || err_json.includes('auth/cancelled-popup-request')) {
        console.log('---- googleLogin cancelled-popup-request')
      } else if (errCode === 'auth/popup-blocked' || err_json.includes('auth/popup-blocked')) {
        setErrorMessage("Popup window blocked by the browser.");
      } else {
        handleError("Failed to start Google sign in, please try again or contact support@lavenirai.com errorCode=gL2", err);
      }
    }
  };

  const samlLogin = async (samlProviderId) => {
    console.log('Starting SAML SSO login with provider:', samlProviderId);
    try {
      const samlProvider = new SAMLAuthProvider(samlProviderId);
      const userCredential = await signInWithPopup(auth, samlProvider);
      const user = userCredential.user;
      const idTokenResult = await user.getIdTokenResult();
      user.idToken = idTokenResult.token;

      const dbUser = await setCurrentUser(
        idTokenResult.token,
        JSON.stringify(user),
        user.email,
        userCredential
      );

      if (Object.keys(dbUser.data).includes("error")) {
        const message = "User does not exist in this region. Try logging into your assigned region or contact support@lavenirai.com. Regions are app.lavenirai.com, app-eu.lavenirai.com, app-sg.lavenirai.com errorCode=L5";
        handleError(message, dbUser.data);
        return;
      }

      await loadUserApp(idTokenResult);
      await loadSession(idTokenResult, user);
      navigate('/');
    } catch (err) {
      console.error('SAML SSO login error:', err);
      const errCode = err?.code || '';
      if (errCode === 'auth/popup-closed-by-user' || errCode === 'auth/cancelled-popup-request') {
        console.log('---- samlLogin popup closed/cancelled');
      } else if (errCode === 'auth/popup-blocked') {
        console.log('---- samlLogin popup blocked');
        setErrorMessage("Popup window blocked by the browser.");
      } else {
        handleError("Failed to start SSO sign in, please contact support@lavenirai.com errorCode=sL1", err);
      }
    }
  };

  const promptForPassword = () => {
    setErrorMessage('Please enter your password.');
    passwordRef.current?.focus();
  };

  // Single entry point for the Login button. One /check-signin-method call
  // decides where the account goes; SAML accounts never need a password, so the
  // button only requires a valid email address to be clickable.
  const handleSubmit = async () => {
    // Close any open confirmation dialogs
    setShowGoogleConfirmation(false);
    setShowEntraIDConfirmation(false);
    setErrorMessage(null);

    if (!isValidEmailAddress()) return;

    let result;
    setLoading(true);
    try {
      result = await resolveSignInMethod(email, 'saml');
    } catch (err) {
      // Network or unexpected error — fall through to email/password sign in
      console.error('handleSubmit resolveSignInMethod error:', err);
      setLoading(false);
      if (password.length < 6) return promptForPassword();
      return handleAuthentication(signInWithEmailAndPassword);
    }
    setLoading(false);

    if (!result.userFound) {
      handleError("User not found. Please register or contact support@lavenirai.com errorCode=L1");
      return;
    }

    if (result.originalRegisterMethod === 'saml') {
      if (!result.samlProviderId) {
        // SAML user but no provider ID found in registry — block login
        handleError("SSO configuration error for your organization, please contact support@lavenirai.com errorCode=sL2");
        return;
      }
      setLoading(true);
      try {
        await samlLogin(result.samlProviderId);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Google/EntraID accounts get their "use <provider> instead" message from
    // handleAuthentication without needing a password typed first.
    const registerMethod = result.originalRegisterMethod;
    if ((registerMethod === 'email' || registerMethod == null) && password.length < 6) {
      return promptForPassword();
    }

    // Proceed with email/password authentication
    handleAuthentication(signInWithEmailAndPassword);
  };

  const loadUserApp = async (idTokenResult) => {
    try {
      const userEmail = idTokenResult.claims.email;
      console.log('---- Validating subscription for userEmail:', userEmail);
      const subscriptionStatus = await subscriptionApi.validateUserSubscriptionByEmail(
        idTokenResult.token, 
        userEmail
      );
      
      // Org subscription expired (incl. endDate auto-flip on server): stay logged in,
      // limitedAccess hides Launch App; metrics/settings/renew stay available.
      if (subscriptionStatus.success &&
          subscriptionStatus.hasSubscription &&
          subscriptionStatus.organization?.subscriptionStatus === 'expired') {
        console.log('Org subscription expired — continuing login with limited access');
        await dispatch(setSubscriptionIssueType('org'));
        await dispatch(setLimitedAccess(true));
        const verbosity = await fetchAppVerbosityApi(idTokenResult.token);
        if (!Number.isInteger(verbosity.data.verbosity)) verbosity.data.verbosity = 0;
        if (!Number.isInteger(verbosity.data.reactVerbosity)) verbosity.data.reactVerbosity = 0;
        await dispatch(setAppVerbosity(verbosity.data.verbosity));
        await dispatch(setAppReactVerbosity(verbosity.data.reactVerbosity));
        return true;
      }

      // User seat inactive while org subscription is still active — existing /subscribe flow
      if (subscriptionStatus.success &&
          subscriptionStatus.hasSubscription &&
          subscriptionStatus.user?.subscriptionStatus !== 'active') {
        
        console.log(`User subscription status is ${subscriptionStatus.user?.subscriptionStatus}, redirecting to subscribe page`);
        
        await dispatch(setSubscriptionIssueType('user'));
        
        await signOut(auth);
        await dispatch(setLoginStatus(false));
        await dispatch(resetUser());
        
        await dispatch(setEmail(userEmail));
        await deleteSession();

        await dispatch(setIdToken(idTokenResult.token));
        await dispatch(setOrganization(subscriptionStatus.organization.name));
        
        navigate('/subscribe');
        return false;
      }

      // Handle no subscription case (404)
      if (subscriptionStatus.status === 404) {
        await dispatch(setLimitedAccess(true));
        await dispatch(setLoginStatus(true));
        
        toast.warning(
          'You do not have an active subscription in this region. Some features will be limited.', 
          { autoClose: 6000 }
        );
        
        return true;
      }

      // Handle active subscription case
      if (subscriptionStatus.success && 
          subscriptionStatus.hasSubscription && 
          subscriptionStatus.user?.isActive && 
          subscriptionStatus.user?.subscriptionStatus === 'active') {
        await dispatch(setLimitedAccess(false));
        const verbosity = await fetchAppVerbosityApi(idTokenResult.token);
        if (!Number.isInteger(verbosity.data.verbosity)) verbosity.data.verbosity = 0;
        if (!Number.isInteger(verbosity.data.reactVerbosity)) verbosity.data.reactVerbosity = 0;
        await dispatch(setAppVerbosity(verbosity.data.verbosity));
        await dispatch(setAppReactVerbosity(verbosity.data.reactVerbosity));
        return true;
      }

      // Handle other error cases
      const message = "Your subscription is not active. Please contact support@lavenirai.com errorCode=L6";
      handleError(message, { subscriptionError: true });
      return false;

    } catch (err) {
      console.error('Error in loadUserApp:', err);
      handleError(err.message || 'Failed to load application');
      return false;
    }
  };
  const loadSession = async (idTokenResult, user) => {
    // console.log('--- loadSession')
    try {
      await reloadSession().then(async (res) => {
        user = res.data;
        if (res.data.error) {
          console.log('\n\nLOGIN RELOAD SESSON ERROR (376):', res.data.error, '\n\n');
          const message = "Something failed during login, please contact support@lavenirai.com errorCode=L4";
          handleError(message, res.data.error);
          return null;
        } else {
          // console.log('LOGIN user:', user)
          await dispatch(setIdToken(idTokenResult.token));
          await dispatch(setToken(idTokenResult.token));
          await dispatch(setUser(JSON.stringify(user)));
          await dispatch(setLoginStatus(true));
          await dispatch(setName(user.name));
          await dispatch(setRole(user.role));
          await dispatch(setOrganization(user.organization.name));
          await dispatch(setPrivacy(user.organization.privacy));
          const application = {
            userId: user._id,
            userEmail: user.email,
            organizationId: user.organization._id,
            organizationName: user.organization.name,
            webUiRevision: process.env.REACT_APP_WEBUI_VERSION,
            unityAppRevision: process.env.REACT_APP_WEBGL_BUILD_ID,
            mode: process.env.REACT_APP_MODE,
            loginStatus: true,
            lastLoggedIn: new Date(),
            region: process.env.REACT_APP_REGION_CODE,
            deployServer: process.env.REACT_APP_DEPLOY_SERVER,
          };
          // console.log('LOGIN application:', application)
          await updateApplicationApi(idTokenResult.token, application);
          if (process.env.REACT_APP_SUCCESS_LOGGING === "true") {
            try {
              writeLoginSuccessToS3Api(idTokenResult.token, {email});
            } catch (err) {
              console.log("writeLoginSuccessToS3Api failure (416):", err)
            }
          }
        }
      });
    } catch (err) {
      // HERE MEANS USER FOUND in FB and in DB but could NOT RELOAD SESSION or UPDATE APP
      const message = "Something failed during login, please contact contact support@lavenirai.com errorCode=L3";
      handleError(message, err);
      return null;
    }
  }
  
  const handleLoginByEnter = async (event) => {
    if (event.key === 'Enter' && isValidEmailAddress() && !loading) {
      await handleSubmit();
    }
  }

  const updateEmail = async (value) => {
    await setFormEmail(value);
  }
  const updatePassword = async (value) => {
    await setPassword(value);
  }

  const loginForm = () => {
    return (
      <form className="space-y-4" onSubmit={e => e.preventDefault()}>
        <input
          type="email"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#6c63ff]"
          value={email}
          onChange={(e) => updateEmail(e.target.value)}
          placeholder="Enter your email"
          autoComplete="username"
          onKeyDown={handleLoginByEnter}
        />

        <div className="relative">
          <input
            ref={passwordRef}
            type={showPassword ? 'text' : 'password'}
            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#6c63ff]"
            value={password}
            onChange={(e) => updatePassword(e.target.value)}
            placeholder="Enter your password"
            autoComplete="current-password"
            onKeyDown={handleLoginByEnter}
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            tabIndex={-1}
          >
            {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
          </button>
        </div>

        <div className="text-right">
          <Link to="/forgot/password" className="text-sm text-[#6c63ff] hover:underline">
            Forgot Password?
          </Link>
        </div>

        <button
          type="submit"
          className="w-full py-2 px-4 bg-[#6c63ff] text-white rounded-md hover:bg-[#5b54e0] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleSubmit}
          disabled={!isValidEmailAddress() || loading}
        >
          Login
        </button>

        <div className="text-center text-sm text-gray-500">OR</div>

        <fieldset className="border border-gray-300 rounded-md px-4 pb-4">
          <legend className="px-2 text-sm text-gray-600">Sign in with SSO</legend>
          <div className="space-y-3">
            <Button
              className="w-full flex items-center justify-center gap-2 py-2 px-4 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              icon={<i className="fab fa-google text-xl"></i>}
              onClick={googleLogin}
            >
              Log in with Google
            </Button>
            <Button
              className="w-full flex items-center justify-center gap-2 py-2 px-4 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              icon={<img className="w-6 h-6" src={MS_ICON} alt="Microsoft" />}
              onClick={msEntraIDLogin}
            >
              Log in with EntraID
            </Button>
          </div>
        </fieldset>

        {errorMessage &&
          <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
            {errorMessage}
          </div>
        }

        {showGoogleConfirmation && (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-gray-700 mb-4">{pendingMessage}</p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowGoogleConfirmation(false)}
                className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowGoogleConfirmation(false);
                  proceedWithGoogleSignIn();
                }}
                className="px-4 py-2 text-white bg-[#6c63ff] rounded-md hover:bg-[#5b54e0]"
              >
                Continue with Google
              </button>
            </div>
          </div>
        )}
        {showEntraIDConfirmation && (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-gray-700 mb-4">{pendingMessage}</p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowEntraIDConfirmation(false)}
                className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowEntraIDConfirmation(false);
                  proceedWithEntraIDSignIn();
                }}
                className="px-4 py-2 text-white bg-[#6c63ff] rounded-md hover:bg-[#5b54e0]"
              >
                Continue with EntraID
              </button>
            </div>
          </div>
        )}
      </form>
    );
  };

  const isValidEmailAddress = () => {
    return !!email.match(
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/    // eslint-disable-line
    ); // eslint-disable-line
  };

  const handleSubscriptionValidation = async (subscriptionStatus) => {
    try {
      // Handle 404 case (no subscription in region)
      if (subscriptionStatus?.status === 404) {
        // Set limited access AFTER setting login status
        await dispatch(setLimitedAccess(true));
        await dispatch(setLoginStatus(true));
        
        toast.warning(
          'You do not have an active subscription in this region. Some features will be limited.', 
          { autoClose: 6000 }
        );
        
        navigate('/home');
        return true;
      }

      // Prevent resetting limitedAccess in other cases
      if (!subscriptionStatus.success || 
          !subscriptionStatus.hasSubscription || 
          !subscriptionStatus.user?.isActive || 
          subscriptionStatus.user?.subscriptionStatus !== 'active') {
        // ... existing error handling ...
        return false;
      }

      // Only reset limitedAccess if we have a valid subscription
      dispatch(setLimitedAccess(false));
      return true;
    } catch (error) {
      console.error('Error in handleSubscriptionValidation:', error);
      return false;
    }
  };

  return (
    <div className="login-background min-h-screen bg-gray-40 flex items-start justify-center pt-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-10 rounded-xl shadow-md">
        <div>
          <h2 className="mt-2 text-center text-3xl font-bold text-gray-700">
            {loading ? "Loading..." : "Login to your account"}
          </h2>
        </div>
        {loginForm()}
      </div>
    </div>
  );

};

export default Login;